/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { useState, useMemo, Fragment, forwardRef } from 'react';
import PropTypes from "prop-types";
import { OuiLoadingSpinner } from '../loading';
import { OuiButton, OuiButtonEmpty, OuiButtonIcon } from '../button';
import { OuiTitle } from '../title';
import { OuiModal, OuiModalBody, OuiModalFooter, OuiModalHeader } from '../modal';
import { OuiI18n, useOuiI18n } from '../i18n';
import { OuiPopover, OuiPopoverTitle } from '../popover';
import { OuiText } from '../text';
import { OuiSpacer } from '../spacer';
// @ts-ignore a react svg
import MarkdownLogo from './icons/markdown_logo';
import { OuiHorizontalRule } from '../horizontal_rule';
import { OuiToolTip } from '../tool_tip';
export var OuiMarkdownEditorFooter = forwardRef((props, ref) => {
  const {
    uiPlugins,
    isUploadingFiles,
    openFiles,
    errors,
    hasUnacceptedItems,
    dropHandlers
  } = props;
  const [isShowingHelp, setIsShowingHelp] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const onButtonClick = () => setIsPopoverOpen(isPopoverOpen => !isPopoverOpen);
  const closePopover = () => setIsPopoverOpen(false);
  let uploadButton;
  const supportedFileTypes = useMemo(() => dropHandlers.map(({
    supportedFiles
  }) => supportedFiles.join(', ')).sort().join(', '), [dropHandlers]);
  const ariaLabels = {
    uploadingFiles: useOuiI18n('ouiMarkdownEditorFooter.uploadingFiles', 'Click to upload files'),
    openUploadModal: useOuiI18n('ouiMarkdownEditorFooter.openUploadModal', 'Open upload files modal'),
    unsupportedFileType: useOuiI18n('ouiMarkdownEditorFooter.unsupportedFileType', 'File type not supported'),
    supportedFileTypes: useOuiI18n('ouiMarkdownEditorFooter.supportedFileTypes', 'Supported files: {supportedFileTypes}', {
      supportedFileTypes
    }),
    showSyntaxErrors: useOuiI18n('ouiMarkdownEditorFooter.showSyntaxErrors', 'Show errors'),
    showMarkdownHelp: useOuiI18n('ouiMarkdownEditorFooter.showMarkdownHelp', 'Show markdown help')
  };
  if (isUploadingFiles) {
    uploadButton = <OuiButtonIcon iconType={OuiLoadingSpinner} aria-label={ariaLabels.uploadingFiles} />;
  } else if (dropHandlers.length > 0 && hasUnacceptedItems) {
    uploadButton = <OuiToolTip content={ariaLabels.supportedFileTypes}>
        <OuiButtonEmpty className="ouiMarkdownEditorFooter__uploadError" autoFocus size="xs" iconType="paperClip" color="danger" aria-label={`${ariaLabels.unsupportedFileType}. ${ariaLabels.supportedFileTypes}. ${ariaLabels.uploadingFiles}`} onClick={openFiles}>
          {ariaLabels.unsupportedFileType}
        </OuiButtonEmpty>
      </OuiToolTip>;
  } else if (dropHandlers.length > 0) {
    uploadButton = <OuiButtonIcon iconType="paperClip" color="text" aria-label={ariaLabels.openUploadModal} onClick={openFiles} />;
  }
  let errorsButton;
  if (errors && errors.length) {
    errorsButton = <OuiPopover button={<OuiButtonEmpty iconType="crossInACircleFilled" size="s" color="danger" aria-label={ariaLabels.showSyntaxErrors} onClick={onButtonClick}>
            {errors.length}
          </OuiButtonEmpty>} isOpen={isPopoverOpen} closePopover={closePopover} panelPaddingSize="s" anchorPosition="upCenter">
        <div className="ouiMarkdownEditorFooter__popover">
          <OuiPopoverTitle>
            <OuiI18n token="ouiMarkdownEditorFooter.errorsTitle" default="Errors" />
          </OuiPopoverTitle>
          {errors.map((message, idx) => <OuiText size="s" key={idx}>
              {message.toString()}
            </OuiText>)}
        </div>
      </OuiPopover>;
  }
  return <div ref={ref} className="ouiMarkdownEditorFooter">
      <div className="ouiMarkdownEditorFooter__actions">
        {uploadButton}
        {errorsButton}
      </div>

      <OuiButtonIcon className="ouiMarkdownEditorFooter__help" iconType={MarkdownLogo} color="text" aria-label={ariaLabels.showMarkdownHelp} onClick={() => setIsShowingHelp(!isShowingHelp)} />
      {isShowingHelp && <OuiModal onClose={() => setIsShowingHelp(false)}>
          <OuiModalHeader>
            <OuiTitle>
              <h3>
                <OuiI18n token="ouiMarkdownEditorFooter.syntaxTitle" default="Syntax help" />
              </h3>
            </OuiTitle>
          </OuiModalHeader>
          <OuiModalBody>
            <OuiText>
              <OuiI18n tokens={['ouiMarkdownEditorFooter.descriptionPrefix', 'ouiMarkdownEditorFooter.descriptionSuffix']} defaults={['This editor uses', 'You can also utilize these additional syntax plugins to add rich content to your text.']}>
                {([descriptionPrefix, descriptionSuffix]) => <p>
                    {descriptionPrefix}{' '}
                    <a href="https://github.github.com/gfm/" target="_blank">
                      Github flavored markdown
                    </a>
                    . {descriptionSuffix}
                  </p>}
              </OuiI18n>
            </OuiText>
            <OuiHorizontalRule />
            {uiPlugins.filter(({
          helpText
        }) => !!helpText).map(({
          name,
          helpText
        }) => <Fragment key={name}>
                  <OuiTitle size="xxs">
                    <p>
                      <strong>{name}</strong>
                    </p>
                  </OuiTitle>
                  <OuiSpacer size="s" />
                  {helpText}
                  <OuiSpacer size="l" />
                </Fragment>)}
            <OuiHorizontalRule />
          </OuiModalBody>
          <OuiModalFooter>
            <OuiButton onClick={() => setIsShowingHelp(false)} fill>
              <OuiI18n token="ouiMarkdownEditorFooter.closeButton" default="Close" />
            </OuiButton>
          </OuiModalFooter>
        </OuiModal>}
    </div>;
});
OuiMarkdownEditorFooter.propTypes = {
  uiPlugins: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    button: PropTypes.shape({
      label: PropTypes.string.isRequired,
      iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addBookmark", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "analyzeEvent", "annotation", "anomalyDetection", "apmApp", "apmTrace", "apps", "appSearchApp", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "bookOpen", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "chatLeft", "chatRight", "check", "checkInCircleEmpty", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "color", "compass", "compute", "console", "consoleApp", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "crossInCircleEmpty", "crossInCircleFilled", "currency", "cut", "dashboard", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentation", "documentEdit", "documents", "dot", "download", "dockedBottom", "dockedDetached", "dockedLeft", "dockedRight", "dockedTakeover", "dockedTop", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "functionAdd", "gear", "generate", "generateContent", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "history", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "integrationGeneral", "integrationObservability", "integrationSearch", "integrationSecurity", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "layers", "lensApp", "lineChart", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOpenSearch", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "polygon", "popout", "power", "pulse", "push", "questionInCircle", "quote", "radar", "radius", "recent", "recentlyViewedApp", "redeploy", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "rocket", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sparkleFilled", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "thumbsDown", "thumbsUp", "timeline", "timelionApp", "timeslider", "training", "trash", "upgradeAssistantApp", "uptimeApp", "undeploy", "unfold", "unlink", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visBuilder", "visBuilderSavedObject", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visQueryDQL", "visQueryPPL", "visQueryPromQL", "visQuerySQL", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram", "wsAnalytics", "wsEssentials", "wsObservability", "wsSearch", "wsSecurityAnalytics", "wsSelector", "navAdministration", "navAiFlow", "navAlerting", "navAnomalyDetection", "navDashboards", "navData", "navDetectionRules", "navDevtools", "navDiscover", "navExperiments", "navGetStarted", "navInfo", "navInfra", "navIntegrations", "navJudgements", "navManage", "navMaps", "navModels", "navNotebooks", "navNotifications", "navOverview", "navQuerySets", "navReports", "navSecurityCases", "navSecurityFindings", "navServiceMap", "navServices", "navSlos", "navThreatIntel", "navTicketing", "navUi", "navSearchConfigurationsln"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired
    }).isRequired,
    helpText: PropTypes.node,
    formatting: PropTypes.shape({
      prefix: PropTypes.string,
      suffix: PropTypes.string,
      blockPrefix: PropTypes.string,
      blockSuffix: PropTypes.string,
      multiline: PropTypes.bool,
      replaceNext: PropTypes.string,
      prefixSpace: PropTypes.bool,
      scanFor: PropTypes.string,
      surroundWithNewlines: PropTypes.bool,
      orderedList: PropTypes.bool,
      trimFirst: PropTypes.bool
    }),
    editor: PropTypes.elementType
  }).isRequired).isRequired,
  isUploadingFiles: PropTypes.bool.isRequired,
  openFiles: PropTypes.func.isRequired,
  errors: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.any.isRequired, PropTypes.any.isRequired]).isRequired).isRequired,
  hasUnacceptedItems: PropTypes.bool.isRequired,
  dropHandlers: PropTypes.arrayOf(PropTypes.shape({
    supportedFiles: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    accepts: PropTypes.func.isRequired,
    getFormattingForItem: PropTypes.func.isRequired
  }).isRequired).isRequired
};
OuiMarkdownEditorFooter.displayName = 'OuiMarkdownEditorFooter';
try {
  OuiMarkdownEditorFooter.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3077135706/src/src/components/markdown_editor/markdown_editor_footer.tsx',
    description: '',
    displayName: 'OuiMarkdownEditorFooter',
    methods: [],
    props: {
      uiPlugins: {
        defaultValue: null,
        description: '',
        name: 'uiPlugins',
        parent: {
          fileName: 'src/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'OuiMarkdownEditorFooterProps'
        },
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'OuiMarkdownEditorFooterProps'
        }],
        required: true,
        type: {
          name: 'OuiMarkdownEditorUiPlugin<any>[]'
        }
      },
      isUploadingFiles: {
        defaultValue: null,
        description: '',
        name: 'isUploadingFiles',
        parent: {
          fileName: 'src/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'OuiMarkdownEditorFooterProps'
        },
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'OuiMarkdownEditorFooterProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      openFiles: {
        defaultValue: null,
        description: '',
        name: 'openFiles',
        parent: {
          fileName: 'src/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'OuiMarkdownEditorFooterProps'
        },
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'OuiMarkdownEditorFooterProps'
        }],
        required: true,
        type: {
          name: '() => void'
        }
      },
      errors: {
        defaultValue: null,
        description: '',
        name: 'errors',
        parent: {
          fileName: 'src/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'OuiMarkdownEditorFooterProps'
        },
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'OuiMarkdownEditorFooterProps'
        }],
        required: true,
        type: {
          name: 'OuiMarkdownParseError[]'
        }
      },
      hasUnacceptedItems: {
        defaultValue: null,
        description: '',
        name: 'hasUnacceptedItems',
        parent: {
          fileName: 'src/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'OuiMarkdownEditorFooterProps'
        },
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'OuiMarkdownEditorFooterProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      dropHandlers: {
        defaultValue: null,
        description: '',
        name: 'dropHandlers',
        parent: {
          fileName: 'src/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'OuiMarkdownEditorFooterProps'
        },
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'OuiMarkdownEditorFooterProps'
        }],
        required: true,
        type: {
          name: 'OuiMarkdownDropHandler[]'
        }
      },
      ref: {
        defaultValue: null,
        description: '',
        name: 'ref',
        parent: {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'RefAttributes'
        },
        declarations: [{
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'RefAttributes'
        }],
        required: false,
        type: {
          name: 'Ref<HTMLDivElement>'
        }
      }
    },
    extendedInterfaces: ['OuiMarkdownEditorFooterProps', 'Attributes']
  };
} catch (e) {}